import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, Optional } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, FormGroupDirective, NgForm } from '@angular/forms';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { auditTime, filter, fromEvent, map, merge, Observable, startWith, Subscription, tap } from 'rxjs';

type FireWhen = 'always' | 'dirty' | 'touched';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormError = { path: string; key: string; value: any };

@Directive({
  selector: 'form[qbTrackErrors]',
  standalone: false,
})
export class QbTrackErrorsDirective implements AfterViewInit, OnDestroy {
  @Input('qbTrackErrors') formId = 'form';
  @Input() errorLabels?: Record<string, Record<string, string>>;
  @Input() fireWhen: FireWhen = 'touched';

  private sub?: Subscription;

  constructor(
    private readonly el: ElementRef<HTMLFormElement>,
    private readonly zone: NgZone,
    private readonly translateService: TranslateService,
    private readonly analyticsService: AnalyticsService,
    @Optional() private readonly formGroupDirective?: FormGroupDirective,
    @Optional() private readonly ngForm?: NgForm
  ) {}

  ngAfterViewInit(): void {
    const root: AbstractControl | undefined = this.formGroupDirective?.control ?? this.ngForm?.control;

    if (!root) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      const submit$ = fromEvent(this.el.nativeElement, 'submit');
      const focusout$ = fromEvent<FocusEvent>(this.el.nativeElement, 'focusout');

      // If 'touched' only blur + submit. If not, watch all the tree
      const source$ =
        this.fireWhen === 'touched' ? merge(focusout$, submit$) : merge(this.watchAllControls(root), submit$);

      this.sub = source$
        .pipe(
          startWith(null),
          auditTime(100),
          map((ev: unknown) => {
            const isBlur = !!(ev as FocusEvent)?.type && (ev as FocusEvent).type === 'focusout';
            const blurredPath =
              this.fireWhen === 'touched' && isBlur
                ? this.getPathFromEventTarget((ev as FocusEvent).target as Element)
                : null;

            const all = this.collectErrors(root, this.fireWhen);
            const errors = blurredPath ? all.filter(e => e.path === blurredPath) : all;
            const sig = JSON.stringify(errors);
            return { errors, sig };
          })
        )
        .subscribe(({ errors }) => {
          if (!errors?.length) {
            return;
          }

          for (const e of errors) {
            const path = e.path;
            const ctrl = this.getControlByPath(root, path);
            const errObj = ctrl?.errors ?? { [e.key]: e.value };
            const error_message = this.getErrorMessage(path, errObj);

            if (!error_message) {
              continue;
            }

            const payload = {
              form_field: path,
              error_message,
            };

            this.analyticsService.sendFormError(payload);
            break;
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  // Traverse the entire form tree to search for changes
  private watchAllControls(ctrl: AbstractControl): Observable<unknown> {
    const streams: Observable<unknown>[] = [ctrl.valueChanges, ctrl.statusChanges];

    const asFormGroup = ctrl as FormGroup;
    const asFormArray = ctrl as FormArray;

    if (asFormGroup?.controls) {
      for (const child of Object.values(asFormGroup.controls)) {
        streams.push(this.watchAllControls(child));
      }
    } else if (Array.isArray(asFormArray?.controls)) {
      for (const child of asFormArray?.controls) {
        streams.push(this.watchAllControls(child));
      }
    }

    return merge(...streams);
  }

  // Traverse the entire form tree and builds an array with the errors
  private collectErrors(ctrl: AbstractControl, when: FireWhen, path: string[] = []): FormError[] {
    const out: FormError[] = [];

    const includeThis = when === 'always' || (when === 'dirty' && ctrl.dirty) || (when === 'touched' && ctrl.touched);

    if (ctrl.errors && includeThis) {
      for (const [key, value] of Object.entries(ctrl.errors)) {
        out.push({ path: path.join('.'), key, value });
      }
    }

    const asFormGroup = ctrl as FormGroup;
    const asFormArray = ctrl as FormArray;

    if (asFormGroup?.controls) {
      for (const [name, child] of Object.entries(asFormGroup.controls)) {
        out.push(...this.collectErrors(child, when, [...path, name]));
      }
    } else if (Array.isArray(asFormArray?.controls)) {
      asFormArray.controls.forEach((child, i) => {
        out.push(...this.collectErrors(child, when, [...path, String(i)]));
      });
    }

    return out;
  }

  // Map path -> key -> i18n key.
  // Return translated label or undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getErrorMessage(path: string, errors: any): string | undefined {
    const table = this.errorLabels?.[path];
    if (!table) {
      return;
    }

    for (const key of Object.keys(table)) {
      if (key !== '*' && errors?.[key]) {
        return this.translateService.instant(table[key]);
      }
    }

    if (table['*'] && errors && Object.keys(errors).length) {
      return this.translateService.instant(table['*']);
    }

    const first = Object.values(table)[0];
    return first ? this.translateService.instant(first as string) : undefined;
  }

  // Navigate throw the path (e.g. 'form.sub.field') and return the control
  private getControlByPath(root: AbstractControl, path: string): AbstractControl | null {
    if (!path) {
      return;
    }

    const segments = path.split('.');
    let current: AbstractControl | null = root;

    for (const seg of segments) {
      if (!current) {
        return null;
      }

      if (current instanceof FormGroup) {
        const next = current.controls[seg as keyof typeof current.controls] as unknown as AbstractControl | undefined;
        current = next ?? null;
        continue;
      }

      if (current instanceof FormArray) {
        const idx = Number(seg);
        if (!Number.isInteger(idx)) return null;
        const next = current.at(idx);
        current = next ?? null;
        continue;
      }

      return null;
    }

    return current;
  }

  // Given the target of the blur, returns its path formControlName -> formGroupName -> formArrayName
  private getPathFromEventTarget(targetEl: Element | null): string | null {
    if (!targetEl) {
      return null;
    }

    const controlHost = targetEl.closest('[formcontrolname]');
    if (!controlHost || !this.el.nativeElement.contains(controlHost)) {
      return null;
    }

    const parts: string[] = [];

    const ctrlName = controlHost.getAttribute('formcontrolname');
    if (!ctrlName) {
      return null;
    }
    parts.unshift(ctrlName);

    let cursor: Element | null = controlHost.parentElement;
    while (cursor && this.el.nativeElement.contains(cursor)) {
      const groupName = cursor.getAttribute?.('formgroupname');
      const arrayName = cursor.getAttribute?.('formarrayname');

      if (groupName) {
        parts.unshift(groupName);
      }
      if (arrayName) {
        parts.unshift(arrayName);
      }

      cursor = cursor.parentElement;
    }
    return parts.join('.');
  }
}
